import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Container, Form, Spinner } from 'react-bootstrap';
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';
import PositionPipeline from '@/components/hiring/PositionPipeline';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import { useTerm } from '@/shared/contexts/TermContext';
import { usePreviewLock } from '@/shared/hooks/usePreviewLock';
import { positionService } from '@/shared/services/positionService';
import { positionPath, sectionLabel } from './positionLinks';
import '@/components/hiring/hiring.css';

const PROGRAM = 'la';
const EMPTY_TEXT =
    'You have no LA positions this term. A section appears here once the LA program releases its decisions and has awarded it an LA.';
const NOT_FOUND_TEXT =
    "This section isn't one of your LA positions this term. Choose one of yours above.";
// The dashboard's to-dos link to a section by its old heading id.
const HASH_PREFIX = '#my-position-';

/**
 * A faculty member's LA positions for the current term, one section at a
 * time: the section's places in words, the students who applied as a list,
 * and the selected student beside it. Every section and every student has an
 * address (/my-positions/<section>/<student>), with the list's filter and
 * search in the query, so a link opens the student and Back returns to the
 * same list.
 */
export default function MyPositionsPage() {
    const { currentTermCode, termsLoading } = useTerm();
    const locked = usePreviewLock();
    const { applicationId, candidacyId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const requestRef = useRef(0);

    const load = useCallback(async () => {
        const request = ++requestRef.current;
        setLoading(true);
        setFailed(false);
        try {
            const answer = await positionService.listPositions({
                program: PROGRAM,
                term: currentTermCode,
            });
            if (request !== requestRef.current) return;
            setPositions(answer.positions || []);
        } catch (_err) {
            if (request === requestRef.current) setFailed(true);
        } finally {
            if (request === requestRef.current) setLoading(false);
        }
    }, [currentTermCode]);

    useEffect(() => {
        if (!currentTermCode) return undefined;
        load();
        return () => {
            requestRef.current += 1;
        };
    }, [currentTermCode, load]);

    // With no section in the address, open the one a link named, else the
    // first, and put it in the address so Back and reload keep it.
    useEffect(() => {
        if (loading || failed || applicationId || positions.length === 0) {
            return;
        }
        // A fragment is typed by whoever built the link; a malformed escape
        // must open the first section, not break the page
        let named = null;
        if (location.hash.startsWith(HASH_PREFIX)) {
            try {
                named = decodeURIComponent(
                    location.hash.slice(HASH_PREFIX.length)
                );
            } catch {
                named = null;
            }
        }
        const target =
            positions.find((p) => p.applicationId === named) || positions[0];
        navigate(positionPath(target.applicationId), { replace: true });
    }, [loading, failed, applicationId, positions, location.hash, navigate]);

    const search = searchParams.toString();
    const suffix = search ? `?${search}` : '';
    const listPath = applicationId ? positionPath(applicationId) : null;

    const select = (id, { fromList = false } = {}) => {
        if (!id) {
            if (location.state?.fromList) navigate(-1);
            else navigate(`${listPath}${suffix}`, { replace: true });
            return;
        }
        const to = `${positionPath(applicationId, id)}${suffix}`;
        if (fromList) navigate(to, { state: { fromList: true } });
        else navigate(to, { replace: true, state: location.state });
    };

    const changeView = ({ filter, query }) => {
        const next = new URLSearchParams();
        if (filter) next.set('show', filter);
        if (query) next.set('q', query);
        setSearchParams(next, { replace: true, state: location.state });
    };

    const position = positions.find((p) => p.applicationId === applicationId);

    let body;
    if (termsLoading || (currentTermCode && loading)) {
        body = (
            <div className="py-3" role="status">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading your positions...
            </div>
        );
    } else if (!currentTermCode) {
        body = <p>No current term is set.</p>;
    } else if (failed) {
        body = (
            <Alert variant="danger">
                <p className="mb-2">Your positions couldn&apos;t be loaded.</p>
                <Button variant="outline-danger" onClick={load}>
                    Retry
                </Button>
            </Alert>
        );
    } else if (positions.length === 0) {
        body = <p>{EMPTY_TEXT}</p>;
    } else {
        // On a phone a student is its own screen: the section's header
        // gives way to the student, whose Back link names the section.
        const sectionHeader = candidacyId ? 'd-none d-lg-flex' : 'd-flex';
        body = (
            <>
                {applicationId && !position && <p>{NOT_FOUND_TEXT}</p>}
                <div
                    className={`${sectionHeader} flex-wrap align-items-end justify-content-between column-gap-4 row-gap-2 mb-3`}
                >
                    {position && (
                        <h2 id="my-position-heading" className="h4 mb-0">
                            {sectionLabel(position)}
                            <span className="d-block fs-6 fw-normal text-body-secondary">
                                {[
                                    position.title,
                                    position.crn && `CRN ${position.crn}`,
                                ]
                                    .filter(Boolean)
                                    .join(', ')}
                            </span>
                        </h2>
                    )}
                    {positions.length > 1 && (
                        <Form.Group
                            controlId="my-positions-section"
                            className="col-12 col-md-5 col-lg-4"
                        >
                            <Form.Label className="small mb-1">
                                Your sections
                            </Form.Label>
                            <Form.Select
                                value={position ? applicationId : ''}
                                onChange={(e) =>
                                    navigate(positionPath(e.target.value))
                                }
                            >
                                {!position && (
                                    <option value="">Choose a section</option>
                                )}
                                {positions.map((p) => (
                                    <option
                                        key={p.applicationId}
                                        value={p.applicationId}
                                    >
                                        {`${sectionLabel(p)}: ${p.title}`}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}
                </div>
                {position && (
                    <section aria-labelledby="my-position-heading">
                        <PositionPipeline
                            key={position.applicationId}
                            applicationId={position.applicationId}
                            program={PROGRAM}
                            mode="faculty"
                            headingLevel={3}
                            selectedId={candidacyId || null}
                            onSelect={select}
                            view={{
                                filter: searchParams.get('show') || undefined,
                                query: searchParams.get('q') || '',
                            }}
                            onViewChange={changeView}
                            hrefFor={(id) =>
                                `${positionPath(position.applicationId, id)}${suffix}`
                            }
                            knownPosition={position}
                            backLabel={`Back to ${sectionLabel(position)}`}
                        />
                    </section>
                )}
            </>
        );
    }

    return (
        <Container className="py-4">
            <h1
                className={`mb-3 ${candidacyId ? 'hiring-title-while-open' : ''}`}
            >
                My LA positions
            </h1>
            <PreviewLockNote locked={locked} />
            {body}
        </Container>
    );
}
